import { PageSize } from '@/constant';
import {
	GetWorkerUnauthorizedDepartureLisParams,
	IWorkerUnauthorizedDepartureDetail,
	IWorkerUnauthorizedDepartureListItem,
	TPayloadUpdateSuccess,
} from '@/services';
import { mergeItemUpdated, mergeListItemUpdated } from '@/utils';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import persistReducer from 'redux-persist/es/persistReducer';

import { formatWorkerUnauthorizedDepartureList, RootState } from '..';
import { storage } from '../store.persist';
import {
	ICreateWorkerUnauthorizedDepartureRequest,
	IPayloadDeleteWorkerUnauthorizedDepartureRequest,
	IPayloadGetWorkerUnauthorizedDepartureDetailRequest,
	IPayloadGetWorkerUnauthorizedDepartureDetailSuccess,
	IPayloadGetWorkerUnauthorizedDepartureExcelListRequest,
	IPayloadGetWorkerUnauthorizedDepartureListRequest,
	IPayloadGetWorkerUnauthorizedDepartureListSuccess,
	IWorkerUnauthorizedDepartureDetailStore,
	IWorkerUnauthorizedDepartureListStore,
} from './worker-unauthorized-departure.type';
const InitParams: GetWorkerUnauthorizedDepartureLisParams = {
	page: 1,
	pageSize: PageSize.S,
	searchTypes: [],
};
const initialState = {
	fetching: false,
	excelListFetching: false,
	workerUnauthorizedDepartureList:
		[] as IWorkerUnauthorizedDepartureListStore[],
	page: 1,
	total: 0,
	workerUnauthorizedDepartureDetail:
		{} as IWorkerUnauthorizedDepartureDetailStore,
	params: InitParams,
};

const workerUnauthorizedDepartureSlice = createSlice({
	initialState,
	name: 'workerUnauthorizedDeparture',
	reducers: {
		/**---- GET WORKER_UNAUTHORIZED_DEPARTURE LIST START ---- */
		getWorkerUnauthorizedDepartureListRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerUnauthorizedDepartureListRequest>
		) => {
			state.fetching = true;
			state.params = action.payload;
		},
		getWorkerUnauthorizedDepartureListSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerUnauthorizedDepartureListSuccess>
		) => {
			const { content, page, total } = action.payload;
			state.fetching = false;
			state.workerUnauthorizedDepartureList = content;
			state.page = page;
			state.total = total;
		},
		getWorkerUnauthorizedDepartureListFailure: (state) => {
			state.fetching = false;
			state.total = 0;
			state.workerUnauthorizedDepartureList = [];
		},

		getWorkerUnauthorizedDepartureExcelListRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerUnauthorizedDepartureExcelListRequest>
		) => {
			state.excelListFetching = true;
		},
		getWorkerUnauthorizedDepartureExcelListSuccess: (state) => {
			state.excelListFetching = false;
		},
		getWorkerUnauthorizedDepartureExcelListFailure: (state) => {
			state.excelListFetching = false;
		},
		/**---- GET WORKER_UNAUTHORIZED_DEPARTURE LIST END ---- */

		/**---- DELETE WORKER_UNAUTHORIZED_DEPARTURE START ---- */
		deleteWorkerUnauthorizedDepartureRequest: (
			state,
			action: PayloadAction<IPayloadDeleteWorkerUnauthorizedDepartureRequest>
		) => {
			state.fetching = true;
		},
		deleteWorkerUnauthorizedDepartureSuccess: (state) => {
			state.fetching = false;
		},
		deleteWorkerUnauthorizedDepartureFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE WORKER_UNAUTHORIZED_DEPARTURE END ---- */

		/**---- GET WORKER_UNAUTHORIZED_DEPARTURE DETAIL START ---- */
		getWorkerUnauthorizedDepartureDetailRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerUnauthorizedDepartureDetailRequest>
		) => {
			state.fetching = true;
		},
		getWorkerUnauthorizedDepartureDetailSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerUnauthorizedDepartureDetailSuccess>
		) => {
			const { workerUnauthorizedDepartureDetail } = action.payload;
			state.fetching = false;
			state.workerUnauthorizedDepartureDetail =
				workerUnauthorizedDepartureDetail;
		},
		getWorkerUnauthorizedDepartureDetailFailure: (state) => {
			state.fetching = false;
		},
		/**---- GET WORKER_UNAUTHORIZED_DEPARTURE DETAIL END ---- */

		/**---- CREATE WORKER_UNAUTHORIZED_DEPARTURE START ---- */
		createWorkerUnauthorizedDepartureRequest: (
			state,
			action: PayloadAction<ICreateWorkerUnauthorizedDepartureRequest>
		) => {
			state.fetching = true;
		},
		createWorkerUnauthorizedDepartureSuccess: (state) => {
			state.fetching = false;
		},
		createWorkerUnauthorizedDepartureFailure: (state) => {
			state.fetching = false;
		},
		/**---- CREATE WORKER_UNAUTHORIZED_DEPARTURE END ---- */

		/**---- UPDATE WORKER_UNAUTHORIZED_DEPARTURE START ---- */
		updateWorkerUnauthorizedDepartureListSuccess: (
			state,
			action: PayloadAction<
				TPayloadUpdateSuccess<IWorkerUnauthorizedDepartureListItem>
			>
		) => {
			const { data } = action.payload;
			const workerUnauthorizedDepartureList = [
				...state.workerUnauthorizedDepartureList,
			];
			const content = mergeListItemUpdated<
				IWorkerUnauthorizedDepartureListStore,
				IWorkerUnauthorizedDepartureListItem
			>(workerUnauthorizedDepartureList, data);
			state.workerUnauthorizedDepartureList =
				formatWorkerUnauthorizedDepartureList(content as []);
		},
		updateWorkerUnauthorizedDepartureDetailSuccess: (
			state,
			action: PayloadAction<
				TPayloadUpdateSuccess<IWorkerUnauthorizedDepartureDetail>
			>
		) => {
			const { data } = action.payload;
			const workerUnauthorizedDepartureDetail = {
				...state.workerUnauthorizedDepartureDetail,
			};
			state.workerUnauthorizedDepartureDetail = mergeItemUpdated<
				IWorkerUnauthorizedDepartureDetailStore,
				IWorkerUnauthorizedDepartureDetail
			>(workerUnauthorizedDepartureDetail, data);
		},
		/**---- UPDATE WORKER_UNAUTHORIZED_DEPARTURE END ---- */
	},
});

export const WorkerUnauthorizedDepartureSelectors = {
	selectFetching: (state: RootState) =>
		state.workerUnauthorizedDeparture.fetching,
	selectPage: (state: RootState) => state.workerUnauthorizedDeparture.page,
	selectTotal: (state: RootState) => state.workerUnauthorizedDeparture.total,
	selectList: (state: RootState) =>
		state.workerUnauthorizedDeparture.workerUnauthorizedDepartureList,
	selectDetail: (state: RootState) =>
		state.workerUnauthorizedDeparture.workerUnauthorizedDepartureDetail,
	selectParams: (state: RootState) => state.workerUnauthorizedDeparture.params,
};

export const WorkerUnauthorizedDepartureActions =
	workerUnauthorizedDepartureSlice.actions;

export const WORKER_UNAUTHORIZED_DEPARTURE_PERSIST_KEY =
	'workerUnauthorizedDeparturePersist';
export const WorkerUnauthorizedDepartureReducer = persistReducer(
	{
		key: WORKER_UNAUTHORIZED_DEPARTURE_PERSIST_KEY,
		storage: storage,
		whitelist: ['workerUnauthorizedDepartureDetail'],
	},
	workerUnauthorizedDepartureSlice.reducer
);
